import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import { DEFAULT_PALETTES, PALETTE_IDS, DEFAULT_HEATMAP } from '../theme';

function clonePalette(p) {
  if (!p || !Array.isArray(p.colors)) return null;
  return {
    colors: [...p.colors],
    colorsWithAlpha: [...(p.colorsWithAlpha || [])],
    borderColors: [...(p.borderColors || [])],
    heatmap: p.heatmap ? { ...DEFAULT_HEATMAP, ...p.heatmap } : { ...DEFAULT_HEATMAP },
  };
}

function isValidPalette(p) {
  return p && Array.isArray(p.colors) && p.colors.length > 0
    && Array.isArray(p.colorsWithAlpha) && p.colorsWithAlpha.length > 0
    && Array.isArray(p.borderColors) && p.borderColors.length > 0;
}

// Estructura persistida: { activeId, palettes: { light, dark, pastel } }
function normalizeLoaded(data) {
  if (data && data.activeId && data.palettes && typeof data.palettes === 'object') {
    const palettes = {};
    for (const id of PALETTE_IDS) {
      const src = data.palettes[id] || DEFAULT_PALETTES[id];
          palettes[id] = isValidPalette(src) ? clonePalette(src) : clonePalette(DEFAULT_PALETTES[id]);
        }
    return {
      activeId: PALETTE_IDS.includes(data.activeId) ? data.activeId : 'pastel',
      palettes,
    };
  }
  if (data && isValidPalette(data)) {
    return {
      activeId: 'pastel',
      palettes: {
        light: clonePalette(DEFAULT_PALETTES.light),
        dark: clonePalette(DEFAULT_PALETTES.dark),
        pastel: clonePalette(data),
      },
    };
  }
  return {
    activeId: 'pastel',
    palettes: {
      light: clonePalette(DEFAULT_PALETTES.light),
      dark: clonePalette(DEFAULT_PALETTES.dark),
      pastel: clonePalette(DEFAULT_PALETTES.pastel),
    },
  };
}

const StyleTemplateContext = createContext();

export const useStyleTemplate = () => {
  const ctx = useContext(StyleTemplateContext);
  if (!ctx) {
    throw new Error('useStyleTemplate must be used within StyleTemplateProvider');
  }
  return ctx;
};

export const StyleTemplateProvider = ({
  children,
  loadPalette: loadPaletteFn,
  savePalette: savePaletteFn,
}) => {
  const [activeId, setActiveIdState] = useState('pastel');
  const [palettes, setPalettesState] = useState(() => ({
    light: clonePalette(DEFAULT_PALETTES.light),
    dark: clonePalette(DEFAULT_PALETTES.dark),
    pastel: clonePalette(DEFAULT_PALETTES.pastel),
  }));
  const [siteLogo, setSiteLogoState] = useState('');
  const [reportLogo, setReportLogoState] = useState('');
  const [siteLogoData, setSiteLogoDataState] = useState('');
  const [reportLogoData, setReportLogoDataState] = useState('');
  const [siteTheme, setSiteThemeState] = useState(null);

  useEffect(() => {
    if (typeof loadPaletteFn !== 'function') return;
    let cancelled = false;
    (async () => {
      try {
        const data = await loadPaletteFn();
        if (cancelled) return;
        const { activeId: id, palettes: loaded } = normalizeLoaded(data?.palette ?? null);
        setActiveIdState(id);
        setPalettesState(loaded);
        if (data?.siteLogo != null) setSiteLogoState(String(data.siteLogo));
        if (data?.reportLogo != null) setReportLogoState(String(data.reportLogo));
        if (data?.siteLogoData != null) setSiteLogoDataState(String(data.siteLogoData));
        if (data?.reportLogoData != null) setReportLogoDataState(String(data.reportLogoData));
        if (data?.siteTheme && typeof data.siteTheme === 'object') setSiteThemeState(data.siteTheme);
      } catch (e) {
        if (!cancelled) console.warn('StyleTemplate: error loading palette', e);
      }
    })();
    return () => { cancelled = true; };
  }, [loadPaletteFn]);

  const palette = palettes[activeId] || palettes.pastel;
  const defaultPalette = DEFAULT_PALETTES[activeId] || DEFAULT_PALETTES.pastel;

  const setActiveId = useCallback((id) => {
    if (PALETTE_IDS.includes(id)) setActiveIdState(id);
  }, []);

  const updateColorAt = useCallback((index, hex, alpha = 0.6) => {
    if (index < 0) return;
    const h = hex.startsWith('#') ? hex : `#${hex}`;
    const r = parseInt(h.slice(1, 3), 16);
    const g = parseInt(h.slice(3, 5), 16);
    const b = parseInt(h.slice(5, 7), 16);
    setPalettesState((prev) => {
      const cur = prev[activeId] || prev.pastel;
      const colors = [...(cur.colors || [])];
      const colorsWithAlpha = [...(cur.colorsWithAlpha || [])];
      const borderColors = [...(cur.borderColors || [])];
      const n = Math.max(colors.length, index + 1);
      while (colors.length < n) colors.push(h);
      while (colorsWithAlpha.length < n) colorsWithAlpha.push(`rgba(${r},${g},${b},${alpha})`);
      while (borderColors.length < n) borderColors.push(h);
      colors[index] = h;
      colorsWithAlpha[index] = `rgba(${r},${g},${b},${alpha})`;
      borderColors[index] = h;
      return { ...prev, [activeId]: { ...cur, colors, colorsWithAlpha, borderColors, heatmap: cur.heatmap } };
    });
  }, [activeId]);

  const resetPalette = useCallback(() => {
    setPalettesState((prev) => ({
      ...prev,
      [activeId]: clonePalette(DEFAULT_PALETTES[activeId]),
    }));
  }, [activeId]);

  const updateHeatmapSetting = useCallback((field, value) => {
    setPalettesState((prev) => {
      const cur = prev[activeId] || prev.pastel;
      const heatmap = { ...DEFAULT_HEATMAP, ...(cur.heatmap || {}), [field]: value };
      return { ...prev, [activeId]: { ...cur, heatmap } };
    });
  }, [activeId]);

  const savePalette = useCallback(async (overrides = {}) => {
    if (typeof savePaletteFn !== 'function') return;
    const payload = {
      palette: { activeId, palettes },
      siteLogo: overrides.siteLogo !== undefined ? String(overrides.siteLogo).trim() : siteLogo,
      reportLogo: overrides.reportLogo !== undefined ? String(overrides.reportLogo).trim() : reportLogo,
      siteLogoData: overrides.siteLogoData !== undefined ? String(overrides.siteLogoData) : siteLogoData,
      reportLogoData: overrides.reportLogoData !== undefined ? String(overrides.reportLogoData) : reportLogoData,
      siteTheme: overrides.siteTheme !== undefined ? overrides.siteTheme : siteTheme,
    };
    await savePaletteFn(payload);
    if (typeof loadPaletteFn === 'function') {
      const data = await loadPaletteFn();
      if (data?.siteLogo != null) setSiteLogoState(String(data.siteLogo));
      if (data?.reportLogo != null) setReportLogoState(String(data.reportLogo));
      if (data?.siteLogoData != null) setSiteLogoDataState(String(data.siteLogoData));
      if (data?.reportLogoData != null) setReportLogoDataState(String(data.reportLogoData));
      if (data?.siteTheme && typeof data.siteTheme === 'object') setSiteThemeState(data.siteTheme);
    }
  }, [activeId, palettes, siteLogo, reportLogo, siteLogoData, reportLogoData, siteTheme, savePaletteFn, loadPaletteFn]);

  const heatmap = useMemo(
    () => (palette?.heatmap ? { ...DEFAULT_HEATMAP, ...palette.heatmap } : { ...DEFAULT_HEATMAP }),
    [palette]
  );

  const value = useMemo(() => ({
    palette,
    palettes,
    activeId,
    setActiveId,
    updateColorAt,
    updateHeatmapSetting,
    resetPalette,
    defaultPalette,
    savePalette,
    defaultPalettes: DEFAULT_PALETTES,
    heatmap,
    defaultHeatmap: DEFAULT_HEATMAP,
    siteLogo,
    reportLogo,
    setSiteLogo: setSiteLogoState,
    setReportLogo: setReportLogoState,
    siteLogoData,
    reportLogoData,
    setSiteLogoData: setSiteLogoDataState,
    setReportLogoData: setReportLogoDataState,
    siteTheme,
    setSiteTheme: setSiteThemeState,
  }), [
    palette,
    palettes,
    activeId,
    setActiveId,
    updateColorAt,
    updateHeatmapSetting,
    resetPalette,
    defaultPalette,
    savePalette,
    heatmap,
    siteLogo,
    reportLogo,
    siteLogoData,
    reportLogoData,
    siteTheme,
  ]);

  return (
    <StyleTemplateContext.Provider value={value}>
      {children}
    </StyleTemplateContext.Provider>
  );
};

export default StyleTemplateContext;
